import { FunctionComponent, useEffect, useState } from "react"
import { Alert, Button, Col, Container, Form, ProgressBar, Row } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { Property, Router, RouterType, Unit } from "../data/models"
import useLazyQuery from "../hooks/useLazyQuery"
import useQuery from "../hooks/useQuery"
import StringUtils from "../utils/StringUtils"

interface ICreateRoutersPage {}
const CreateRoutersPage: FunctionComponent<ICreateRoutersPage> = () => {
	const navigate = useNavigate()
	const [property, setProperty] = useState<Property>()
	const [routerType, setRouterType] = useState<RouterType>()
	const [name, setName] = useState<string>("")
	const [model, setModel] = useState<string>("")
	//
	const [progress, setProgress] = useState<number>()
	const [firstUnit, setFirstUnit] = useState<Unit>()
	const [lastUnit, setLastUnit] = useState<Unit>()
	const [publicIpv4Address, setPublicIpv4Address] = useState<string>("")
	const [firstIpv4Address, setFirstIpv4Address] = useState<string>("")
	const [lastIpv4Address, setLastIpv4Address] = useState<string>("")
	const [firstIpv6Address, setFirstIpv6Address] = useState<string>("")
	const [lastIpv6Address, setLastIpv6Address] = useState<string>("")
	const [firstIpv6Prefix, setFirstIpv6Prefix] = useState<string>("")
	const [lastIpv6Prefix, setLastIpv6Prefix] = useState<string>("")
	const propertiesQuery = useQuery<void, PropertiesOutput>(process.env.REACT_APP_KNIT_URL + "properties")
	const unitsQuery = useLazyQuery<void, UnitsOutput>(process.env.REACT_APP_KNIT_URL + "units?propertyId=" + property?.id)
	const createRouterQuery = useLazyQuery<Partial<Router>, any>(process.env.REACT_APP_KNIT_URL + "routers", "POST")
	const uQuery = unitsQuery.query
	useEffect(() => {
		if (routerType === RouterType.Unit && property?.id) uQuery()
	}, [routerType, property?.id, uQuery])
	useEffect(() => {
		if (firstUnit == null && lastUnit == null && unitsQuery.data?.units?.[0] != null) {
			setFirstUnit(unitsQuery.data?.units?.[0])
			setLastUnit(unitsQuery.data?.units?.[unitsQuery.data?.units.length - 1])
		}
	}, [firstUnit, lastUnit, unitsQuery])
	useEffect(() => {
		if (firstIpv4Address && firstUnit && lastUnit) {
			const startIndex = unitsQuery.data?.units.findIndex((u) => u.id === firstUnit.id)
			const endIndex = unitsQuery.data?.units.findIndex((u) => u.id === lastUnit.id)
			const ipv4Address = addIpv4Address(firstIpv4Address, endIndex! - startIndex!)
			if (ipv4Address) setLastIpv4Address(ipv4Address)
		}
	}, [firstIpv4Address, firstUnit, lastUnit, unitsQuery])
	useEffect(() => {
		if (firstIpv6Address && firstUnit && lastUnit) {
			const ipPattern = /^([0-9a-f:]*:)([0-9a-f]{1,4})(::1)$/
			const start = firstIpv6Address.match(ipPattern)?.[1]
			if (start) {
				let middleHex = firstIpv6Address.match(ipPattern)?.[2]!
				setFirstIpv6Prefix(start + middleHex + "::/64")
				let middleNumber = parseInt(middleHex, 16)
				const startIndex = unitsQuery.data?.units.findIndex((u) => u.id === firstUnit.id)!
				const endIndex = unitsQuery.data?.units.findIndex((u) => u.id === lastUnit.id)!
				middleNumber = middleNumber + endIndex - startIndex
				middleHex = middleNumber.toString(16)
				setLastIpv6Address(start + middleHex + "::1")
				setLastIpv6Prefix(start + middleHex + "::/64")
			}
		}
	}, [firstIpv6Address, firstUnit, lastUnit, unitsQuery])
	//
	const setPropertyId = (id: string) => {
		setProperty(propertiesQuery.data?.properties?.find((p) => p.id === Number(id)))
	}
	const setFirstUnitId = (id: string) => {
		setFirstUnit(unitsQuery.data?.units?.find((u) => u.id === Number(id)))
	}
	const setLastUnitId = (id: string) => {
		setLastUnit(unitsQuery.data?.units?.find((u) => u.id === Number(id)))
	}
	const createRouters = async () => {
		const firstIndex = unitsQuery.data?.units.findIndex((u) => u.id === firstUnit?.id)!
		const lastIndex = unitsQuery.data?.units.findIndex((u) => u.id === lastUnit?.id)!
		for (let index = firstIndex; index <= lastIndex; index++) {
			const unit = unitsQuery.data?.units[index]!
			console.log(`Create Router for ${unit.name}`)
			setProgress((index - firstIndex) / (lastIndex - firstIndex))
			await createRouterQuery.query({
				body: {
					propertyId: property?.id,
					propertyName: property?.name,
					unitId: unit.id,
					unitName: unit.name,
					type: routerType,
					model: model,
					name: name + unit.name,
					domain: undefined,
					publicIpv4Address: publicIpv4Address || undefined,
					ipv4Address: addIpv4Address(firstIpv4Address, index - firstIndex)!,
					ipv6Address: addIpv6AddressBlocks(firstIpv6Address, index - firstIndex)!,
					delegatedIpv6Prefix: addIpv6AddressBlocks(firstIpv6Address, index - firstIndex)?.replace("::1", "::/64"),
					username: "admin",
					password: StringUtils.random(24, "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890"),
					pppoeUsername: name + unit.name,
					pppoePassword: StringUtils.random(24, "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890"),
					vpnUsername: undefined,
					vpnPassword: undefined,
					wifiName: name + unit.name,
					wifiPassword: StringUtils.random(12, "abcdefghijkmnpqrstuvwxyz23456789"),
				},
			})
		}
		setProgress(undefined)
	}
	//
	const error = propertiesQuery.error || unitsQuery.error
	const loading = false
	//
	if (loading) {
		return (
			<Container>
				<h2 className="text-center mt-4">Create Routers</h2>
				<div className="text-center">Loading</div>
			</Container>
		)
	}
	return (
		<Container>
			<h2 className="text-center mt-4">Create Routers</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<Row className="mt-4">
				<Col>
					<Form.Group className="mb-3" controlId="property">
						<Form.Label>Property</Form.Label>
						<Form.Select required value={property?.id} onChange={(e) => setPropertyId(e.target.value)}>
							<option></option>
							{propertiesQuery.data?.properties?.map((p) => (
								<option key={p.id} value={p.id}>
									{p.name}
								</option>
							))}
						</Form.Select>
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="property">
						<Form.Label>Router Type</Form.Label>
						<Form.Select required value={routerType} onChange={(e) => setRouterType(e.target.value as RouterType)}>
							<option></option>
							<option value={RouterType.Property}>Property</option>
							<option value={RouterType.Unit}>Unit</option>
						</Form.Select>
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="model">
						<Form.Label>Name</Form.Label>
						<Form.Control type="text" required value={name} onChange={(e) => setName(e.target.value)} />
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="model">
						<Form.Label>Model</Form.Label>
						<Form.Control type="text" required value={model} onChange={(e) => setModel(e.target.value)} />
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col>
					{routerType === RouterType.Unit && (
						<Form.Group className="mb-3" controlId="property">
							<Form.Label>First Unit</Form.Label>
							<Form.Select required value={firstUnit?.id} onChange={(e) => setFirstUnitId(e.target.value)}>
								<option></option>
								{unitsQuery.data?.units?.map((u) => (
									<option key={u.id} value={u.id}>
										{u.name}
									</option>
								))}
							</Form.Select>
						</Form.Group>
					)}
				</Col>
				<Col>
					{routerType === RouterType.Unit && (
						<Form.Group className="mb-3" controlId="property">
							<Form.Label>Last Unit</Form.Label>
							<Form.Select required value={lastUnit?.id} onChange={(e) => setLastUnitId(e.target.value)}>
								<option></option>
								{unitsQuery.data?.units?.map((u) => (
									<option key={u.id} value={u.id}>
										{u.name}
									</option>
								))}
							</Form.Select>
						</Form.Group>
					)}
				</Col>
			</Row>
			<Row>
				<Col>
					<Form.Group className="mb-3" controlId="model">
						<Form.Label>Public IPv4 Address</Form.Label>
						<Form.Control type="text" required value={publicIpv4Address} onChange={(e) => setPublicIpv4Address(e.target.value)} />
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="model">
						<Form.Label>First IPv4 Address</Form.Label>
						<Form.Control type="text" required value={firstIpv4Address} onChange={(e) => setFirstIpv4Address(e.target.value)} />
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="model">
						<Form.Label>Last IPv4 Address</Form.Label>
						<Form.Control type="text" readOnly value={lastIpv4Address} />
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col>
					<Form.Group className="mb-3" controlId="model">
						<Form.Label>First IPv6 Address</Form.Label>
						<Form.Control type="text" required value={firstIpv6Address} onChange={(e) => setFirstIpv6Address(e.target.value)} />
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="model">
						<Form.Label>First IPv6 Prefix</Form.Label>
						<Form.Control type="text" readOnly value={firstIpv6Prefix} />
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="model">
						<Form.Label>Last IPv6 Address</Form.Label>
						<Form.Control type="text" readOnly value={lastIpv6Address} />
					</Form.Group>
				</Col>
				<Col>
					<Form.Group className="mb-3" controlId="model">
						<Form.Label>Last IPv6 Prefix</Form.Label>
						<Form.Control type="text" readOnly value={lastIpv6Prefix} />
					</Form.Group>
				</Col>
			</Row>
			{progress != null && (
				<Row>
					<ProgressBar now={progress * 100} />
				</Row>
			)}
			<Row className="mt-4">
				<Col xs="auto">
					<Button variant="secondary" disabled={loading} onClick={() => navigate(-1)}>
						Cancel
					</Button>
				</Col>
				<Col></Col>
				<Col xs="auto">
					<Button variant="primary" disabled={loading || !!error} onClick={createRouters}>
						{loading ? "Saving" : "Confirm"}
					</Button>
				</Col>
			</Row>
		</Container>
	)
}

export default CreateRoutersPage

interface PropertiesOutput {
	properties: Property[]
}

interface UnitsOutput {
	units: Unit[]
}

const addIpv4Address = (ipv4Address: string, value: number) => {
	const ipPattern = /^([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})$/
	const matches = ipv4Address.match(ipPattern)
	if (!matches) return null
	const segments = [Number(matches[1]), Number(matches[2]), Number(matches[3]), Number(matches[4])]
	if (isNaN(segments[0]) || isNaN(segments[1]) || isNaN(segments[2]) || isNaN(segments[3])) return null
	segments[3] += value
	while (segments[3] > 255) {
		segments[2] += 1
		segments[3] -= 256
	}
	while (segments[2] > 255) {
		segments[1] += 1
		segments[2] -= 256
	}
	while (segments[1] > 255) {
		segments[0] += 1
		segments[2] -= 256
	}
	return segments[0] + "." + segments[1] + "." + segments[2] + "." + segments[3]
}

/**
 * Adds a number of /64 blocks to the existing address, so the 4th segment from the left is the first one that gets incremented
 * @param ipv6Address must be an address in a /64 block ending in ::1
 * @param value
 */
const addIpv6AddressBlocks = (ipv6Address: string, value: number) => {
	const ipPattern = /^([0-9a-f:]{1,4}):([0-9a-f:]{1,4}):([0-9a-f:]{1,4}):([0-9a-f]{1,4})::1$/
	const matches = ipv6Address.match(ipPattern)
	if (!matches) return null
	const hexSegments = [matches[1], matches[2], matches[3], matches[4]]
	const decSegments = [parseInt(hexSegments[0], 16), parseInt(hexSegments[1], 16), parseInt(hexSegments[2], 16), parseInt(hexSegments[3], 16)]
	decSegments[3] += value
	while (decSegments[3] > 65535) {
		decSegments[2] += 1
		decSegments[3] -= 65536
	}
	while (decSegments[2] > 65535) {
		decSegments[1] += 1
		decSegments[2] -= 65536
	}
	while (decSegments[1] > 65535) {
		decSegments[0] += 1
		decSegments[2] -= 65536
	}
	hexSegments[0] = decSegments[0].toString(16)
	hexSegments[1] = decSegments[1].toString(16)
	hexSegments[2] = decSegments[2].toString(16)
	hexSegments[3] = decSegments[3].toString(16)
	return hexSegments[0] + ":" + hexSegments[1] + ":" + hexSegments[2] + ":" + hexSegments[3] + "::1"
}
